import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Pencil, Target as TargetIcon, TrendingUp, Users, PhoneCall, LayoutGrid, Trophy, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AppHeader from "@/components/AppHeader";
import GoalWizard, { computeTargets } from "@/components/GoalWizard";
import DailyTracker from "@/components/DailyTracker";
import WeeklyTracker from "@/components/WeeklyTracker";
import GamificationStrip from "@/components/GamificationStrip";
import Leaderboard from "@/components/Leaderboard";
import { api } from "@/lib/api";
import { inr, numIN } from "@/lib/format";

export default function CoachDashboard() {
  const [goal, setGoal] = useState(undefined);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("tracker");
  const [board, setBoard] = useState(null);
  const [cohort, setCohort] = useState("all");

  const load = useCallback(async () => {
    const [g, e, s] = await Promise.all([api.get("/goal"), api.get("/entries"), api.get("/summary")]);
    setGoal(g.data);
    setEntries(e.data);
    setSummary(s.data);
  }, []);

  const loadBoard = useCallback(async (c = "all") => {
    const { data } = await api.get("/leaderboard", { params: c && c !== "all" ? { cohort: c } : {} });
    setBoard(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "leaderboard") loadBoard(cohort);
  }, [tab, cohort, loadBoard]);

  const t = goal ? computeTargets(goal.goal_amount, goal.price_per_client) : null;
  const pct = goal && summary ? Math.min(100, Math.round((summary.total_money / goal.goal_amount) * 100)) : 0;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader subtitle="Health Coach workspace" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        {goal === undefined ? (
          <div className="h-64 flex items-center justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin" />
          </div>
        ) : !goal || editing ? (
          <div className="space-y-6">
            {editing && (
              <Button variant="ghost" onClick={() => setEditing(false)} data-testid="cancel-edit-goal-btn">← Back to dashboard</Button>
            )}
            <GoalWizard existing={goal} onSaved={() => { setEditing(false); load(); }} />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="space-y-8">
            <TabsList data-testid="coach-tabs">
              <TabsTrigger value="tracker" data-testid="coach-tab-tracker"><LayoutGrid className="h-4 w-4 mr-2" /> My Tracker</TabsTrigger>
              <TabsTrigger value="weekly" data-testid="coach-tab-weekly"><CalendarRange className="h-4 w-4 mr-2" /> Weekly</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="coach-tab-leaderboard"><Trophy className="h-4 w-4 mr-2" /> Leaderboard</TabsTrigger>
            </TabsList>

            <TabsContent value="tracker" className="space-y-8">
              {/* Hero progress */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                          className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm" data-testid="coach-goal-summary">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-1">90-day earning goal</div>
                    <div className="font-mono-num text-4xl sm:text-5xl font-extrabold text-slate-900">{inr(goal.goal_amount)}</div>
                    <div className="text-sm text-muted-foreground mt-2">Started {goal.start_date} · {inr(goal.price_per_client)} per client</div>
                  </div>
                  <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-goal-btn">
                    <Pencil className="h-4 w-4 mr-2" /> Edit goal
                  </Button>
                </div>
                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Earned so far</span>
                    <span className="font-mono-num font-semibold text-emerald-700">{inr(summary.total_money)} · {pct}%</span>
                  </div>
                  <Progress value={pct} className="h-3" data-testid="coach-progress-bar" />
                </div>
              </motion.div>

              <DailyTracker entries={entries} onChange={load} />

              {/* Gamification */}
              <GamificationStrip summary={summary} />

              {/* Weekly targets + totals */}
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-4">My every-week target</div>
                  <div className="grid grid-cols-3 gap-4">
                    <Metric label="People to reach" value={numIN(t.weekly_people)} testid="weekly-target-people" />
                    <Metric label="Session 0s to do" value={numIN(t.weekly_session0)} testid="weekly-target-session0" />
                    <Metric label="Money to earn" value={inr(t.weekly_money)} testid="weekly-target-money" />
                  </div>
                </div>
                <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                  <Stat icon={Users} label="People reached" value={numIN(summary.total_people)} of={`of ${numIN(t.outreach_90)}`} testid="total-people" />
                  <Stat icon={PhoneCall} label="Session 0s done" value={numIN(summary.total_session0)} of={`of ${numIN(t.session0_90)}`} testid="total-session0" />
                  <Stat icon={TrendingUp} label="Clients joined" value={numIN(summary.total_clients)} of={`of ${numIN(t.clients_90)}`} testid="total-clients" />
                  <Stat icon={TargetIcon} label="Days logged" value={numIN(summary.entry_count)} of="entries" testid="total-days" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="weekly" className="space-y-8">
              <WeeklyTracker />
            </TabsContent>

            <TabsContent value="leaderboard" className="space-y-8">
              <GamificationStrip summary={summary} />
              {board ? (
                <Leaderboard rows={board.rows} myId={board.my_id} myRow={board.my_row} cohorts={board.cohorts}
                             cohort={cohort} onCohort={setCohort} />
              ) : (
                <div className="h-40 flex items-center justify-center">
                  <div className="h-8 w-8 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin" />
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono-num text-2xl sm:text-3xl font-bold text-emerald-900">{value}</div>
      <div className="text-sm text-emerald-800 mt-1">{label}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, of, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={testid}>
      <Icon className="h-4 w-4 text-amber-600 mb-2" />
      <div className="font-mono-num text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium">{label}</div>
      <div className="text-xs text-muted-foreground">{of}</div>
    </div>
  );
}
